import React, { useState, useEffect } from 'react';
import { Select, Radio, ConfigProvider, DatePicker } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';

const { Option, OptGroup } = Select;
const { RangePicker } = DatePicker;

dayjs.locale('ru');

// Панель фильтров для вкладки с графиками аналитики
// Содержит выбор завода, навигацию стрелками по заводам,
// выбор показателя и диапазон дат для графиков
const FilterPanel = ({ factories, onFactorySelect, onIndicatorChange, chartStartDate, chartEndDate, onDateRangeChange }) => {
    // Индекс текущего выбранного завода в плоском списке фабрик
    const [selectedFactoryIndex, setSelectedFactoryIndex] = useState(0);  // Индекс текущего завода

    // Упрощаем доступ к списку всех заводов: разворачиваем кластеры в один массив заводов
    const flattenedFactories = factories.flatMap(cluster => cluster.factories);

    // Выбор завода на основе индекса: при изменении индекса уведомляем родительский компонент
    useEffect(() => {
        if (flattenedFactories.length > 0) {
            onFactorySelect(flattenedFactories[selectedFactoryIndex]);
        }
    }, [selectedFactoryIndex, flattenedFactories, onFactorySelect]);

    // Обработчики для кнопок переключения завода влево/вправо
    const handlePreviousFactory = () => {
        setSelectedFactoryIndex(prevIndex =>
            prevIndex > 0 ? prevIndex - 1 : flattenedFactories.length - 1  // Зацикливаем на последнем заводе
        );
    };

    const handleNextFactory = () => {
        setSelectedFactoryIndex(prevIndex =>
            prevIndex < flattenedFactories.length - 1 ? prevIndex + 1 : 0  // Зацикливаем на первом заводе
        );
    };

    // Обработчик нажатия клавиш для перемещения по списку заводов стрелками влево/вправо
    const handleKeyDown = (e) => {
        // Проверяем, что элемент, который в фокусе, не является радио-кнопкой,
        // чтобы не перехватывать стрелки у Radio.Group
        const activeElement = document.activeElement;
        if (activeElement.tagName.toLowerCase() === 'input' && activeElement.type === 'radio') {
            return;  // Игнорируем нажатие стрелок для радио-кнопок
        }

        if (e.key === 'ArrowLeft') {
            handlePreviousFactory();  // Перемещение на предыдущий завод
        } else if (e.key === 'ArrowRight') {
            handleNextFactory();  // Перемещение на следующий завод
        }
    };

    // Добавляем обработчик события нажатия клавиш при монтировании компонента
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFactoryIndex]);  // Обновляем обработчик при изменении индекса

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '20px',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #ddd',
            width: '100%',
            boxSizing: 'border-box',
            gap: '16px',
        }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* Кнопка влево для переключения завода */}
                <button
                    className="monthly-reports-page__arrow-button"
                    onClick={handlePreviousFactory}
                    style={{ cursor: 'pointer', padding: '10px' }}>
                    ◀
                </button>

                {/* Выпадающий список для выбора завода */}
                <Select
                    showSearch
                    placeholder="Выберите завод"
                    optionFilterProp="children"
                    value={flattenedFactories[selectedFactoryIndex]?.id}  // Выбранный завод
                    onChange={(value) => {
                        const index = flattenedFactories.findIndex(factory => factory.id === value);
                        if (index !== -1) {
                            setSelectedFactoryIndex(index);
                        }
                    }}
                    style={{ width: '300px'}}
                    filterOption={(input, option) =>
                        option.children && typeof option.children === 'string' &&
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0  // Добавлена проверка на существование и тип данных
                    }
                >
                    {factories.map(cluster => (
                        <OptGroup key={cluster.id} label={cluster.name}>
                            {cluster.factories.map(factory => (
                                <Option key={factory.id} value={factory.id}>
                                    {factory.name}
                                </Option>
                            ))}
                        </OptGroup>
                    ))}
                </Select>

                {/* Кнопка вправо для переключения завода */}
                <button
                    className="monthly-reports-page__arrow-button"
                    onClick={handleNextFactory}
                    style={{ cursor: 'pointer', padding: '10px' }}
                    >
                    ▶
                </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Радио-кнопки для выбора показателя (какая метрика отображается на графике) */}
                <Radio.Group onChange={onIndicatorChange} defaultValue="all">
                    <Radio value="city">В городе</Radio>
                    <Radio value="city_mvo">В городе + МВО</Radio>
                    <Radio value="all">Все</Radio>
                </Radio.Group>

                {/* Выбор диапазона дат для отображения данных на графиках */}
                <ConfigProvider locale={ruRU}>
                    <RangePicker
                        value={
                            chartStartDate && chartEndDate
                                ? [
                                    dayjs(chartStartDate, 'YYYY-MM-DD'),
                                    dayjs(chartEndDate, 'YYYY-MM-DD'),
                                ]
                                : null
                        }
                        onChange={(values) => {
                            if (!values || values.length !== 2) {
                                onDateRangeChange('', '');
                                return;
                            }

                            const nextStart = values[0].format('YYYY-MM-DD');
                            const nextEnd = values[1].format('YYYY-MM-DD');

                            onDateRangeChange(nextStart, nextEnd);
                        }}
                        disabledDate={(current) => {
                            if (!current) return false;
                            return current.isAfter(dayjs().endOf('day'));
                        }}
                    />
                </ConfigProvider>
            </div>
        </div>
    );
};

export default FilterPanel;
